import { useQuery } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { arrUniqEl } from 'infrastructure/functions';
import { PatientCallHistory } from 'infrastructure/classes/patient/call-history';
import { useState } from 'react';

import useApi from '../use-api';

import type { IPatientCallHistoryResponse } from 'infrastructure/interfaces/i-patient-call-history';
import type {
  IPaginationMeta,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

const api = `reports/reports/call-history`;

interface IValues {
  loading: boolean;
  items: PatientCallHistory[];
  meta: IPaginationMeta;
  setMeta: (params: IPartialPaginationMeta) => void;
}

interface IProps {
  patientGuid: string;
  meta?: IPartialPaginationMeta;
}

export const usePatientCallHistory = (props: IProps): IValues => {
  const { patientGuid } = props;
  const { items = 10, page = 1 } = props?.meta ?? {};
  const { loadData } = useApi({ showError: false });
  const [meta, setMeta] = useState<IPaginationMeta>({
    items,
    page,
    totalCount: 0,
    sort: 'desc',
  });

  const queryKey = [api, patientGuid, meta];

  const { data, isLoading } = useQuery({
    queryKey,
    staleTime: STALE_TIME,
    queryFn: () =>
      loadData<
        IPatientCallHistoryResponse,
        Omit<IPaginationMeta, 'sortField' | 'totalCount'>
      >(api, {
        sort: meta?.sort,
        items,
        page,
        patientGuid,
      }).then((res) => {
        if (res) {
          setMeta({
            ...meta,
            totalCount: res.count,
          });
          return {
            count: res.count,
            items: arrUniqEl(res.items, 'callId').map(
              (el) => new PatientCallHistory(el),
            ),
          };
        }
      }),
  });

  const handleSetMeta = (params: IPartialPaginationMeta) => {
    setMeta({ ...meta, ...params });
  };

  return {
    meta,
    items: data ? data.items : [],
    loading: isLoading,
    setMeta: handleSetMeta,
  };
};
